import { useState } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { Mission } from '../../data/missions';
import HowToUse from '../HowToUse/HowToUse';
import UploadBox from '../UploadBox/UploadBox';
import StatusBar from '../StatusBar/StatusBar';
import ExcelMergerPanel from '../ExcelMergerPanel/ExcelMergerPanel';
import RentalConverterPanel from '../RentalConverterPanel/RentalConverterPanel';
import AmazonSalesPanel from '../AmazonSalesPanel/AmazonSalesPanel';
import PayrollJournalPanel from '../PayrollJournalPanel/PayrollJournalPanel';
import CashVaultPanel from '../CashVaultPanel/CashVaultPanel';
import HrConsolidatorPanel from '../HrConsolidatorPanel/HrConsolidatorPanel';
import AttendancePanel from '../AttendancePanel/AttendancePanel';
import SupplierGroupingPanel from '../SupplierGroupingPanel/SupplierGroupingPanel';
import RemittanceReaderPanel from '../RemittanceReaderPanel/RemittanceReaderPanel';
import ExpenditureLedgerPanel from '../ExpenditureLedgerPanel/ExpenditureLedgerPanel';
import AccountsCheckerPanel from '../AccountsCheckerPanel/AccountsCheckerPanel';
import RemittancesPanel from '../RemittancesPanel/RemittancesPanel';
import IntelFusionPanel from '../IntelFusionPanel/IntelFusionPanel';
import SumupReconPanel from '../SumupReconPanel/SumupReconPanel';
import ReconToolsPanel from '../ReconToolsPanel/ReconToolsPanel';
import FixedAssetsPanel from '../FixedAssetsPanel/FixedAssetsPanel';
import BankReconPanel from '../BankReconPanel/BankReconPanel';
import AgedPayablesPanel from '../AgedPayablesPanel/AgedPayablesPanel';
import WeeklySalesPanel from '../WeeklySalesPanel/WeeklySalesPanel';
import SupplierReconPanel from '../SupplierReconPanel/SupplierReconPanel';
import DataAnonymizerPanel from '../DataAnonymizerPanel/DataAnonymizerPanel';
import SmeTrackerPanel from '../SmeTrackerPanel/SmeTrackerPanel';
import RevenueSplitterPanel from '../RevenueSplitterPanel/RevenueSplitterPanel';
import PayrollMatchPanel from '../PayrollMatchPanel/PayrollMatchPanel';
import NotesQueriesPanel from '../NotesQueriesPanel/NotesQueriesPanel';
import UnmergePanel from '../UnmergePanel/UnmergePanel';
import PayoutReconPanel from '../PayoutReconPanel/PayoutReconPanel';
import MisRefreshPanel from '../MisRefreshPanel/MisRefreshPanel';
import InnControlPanel from '../InnControlPanel/InnControlPanel';
import EtbInterlinkPanel from '../EtbInterlinkPanel/EtbInterlinkPanel';
import LedgerTransformerPanel from '../LedgerTransformerPanel/LedgerTransformerPanel';
import VatAgentPanel from '../VatAgentPanel/VatAgentPanel';
import TrainerScoringPanel from '../TrainerScoringPanel/TrainerScoringPanel';
import TrainingEvalPanel from '../TrainingEvalPanel/TrainingEvalPanel';
import PayrollAgentPanel from '../PayrollAgentPanel/PayrollAgentPanel';
import ClientTransformerPanel from '../ClientTransformerPanel/ClientTransformerPanel';
import LedgerPdfPanel from '../LedgerPdfPanel/LedgerPdfPanel';
import MaReportPanel from '../MaReportPanel/MaReportPanel';
import MaReportV2Panel from '../MaReportV2Panel/MaReportV2Panel';
import AsWorkflowPanel from '../AsWorkflowPanel/AsWorkflowPanel';
import { Button } from '@/components/ui/button';

const DEFAULT_STEPS = [
  'Confirm this is the mission that matches your data.',
  'Upload the file in the accepted format below.',
  'Wait while it is processed and review the returned results.',
];

interface MissionPanelProps {
  mission: Mission;
  onBack: () => void;
}

function MissionPanel({ mission, onBack }: MissionPanelProps) {
  const [file, setFile] = useState<File | null>(null);

  if (mission.id === 'excel-forge') {
    return <ExcelMergerPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'rental-converter') {
    return <RentalConverterPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'amazon-sales') {
    return <AmazonSalesPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'payroll-lab') {
    return <PayrollJournalPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'cash-vault') {
    return <CashVaultPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'hr-consolidator') {
    return <HrConsolidatorPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'attendance') {
    return <AttendancePanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'supplier-grouping') {
    return <SupplierGroupingPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'remittance-reader') {
    return <RemittanceReaderPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'expenditure-ledger') {
    return <ExpenditureLedgerPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'accounts-checker') {
    return <AccountsCheckerPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'remittances') {
    return <RemittancesPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'intel-fusion') {
    return <IntelFusionPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'sumup-recon') {
    return <SumupReconPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'reconciliation-tools') {
    return <ReconToolsPanel />;
  }

  if (mission.id === 'fixed-assets') {
    return <FixedAssetsPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'bank-recon') {
    return <BankReconPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'aged-payables') {
    return <AgedPayablesPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'weekly-sales') {
    return <WeeklySalesPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'supplier-recon') {
    return <SupplierReconPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'data-vault') {
    return <DataAnonymizerPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'sme-tracker') {
    return <SmeTrackerPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'revenue-splitter') {
    return <RevenueSplitterPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'payroll-match') {
    return <PayrollMatchPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'notes-queries') {
    return <NotesQueriesPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'unmerge-realign') {
    return <UnmergePanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'payout-recon') {
    return <PayoutReconPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'mis-refresh') {
    return <MisRefreshPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'inncontrol') {
    return <InnControlPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'etb-interlink') {
    return <EtbInterlinkPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'ledger-transformer') {
    return <LedgerTransformerPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'vat-agent') {
    return <VatAgentPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'trainer-scoring') {
    return <TrainerScoringPanel mission={mission} onBack={onBack} />;
  }
  if (mission.id === 'training-eval') {
    return <TrainingEvalPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'payroll-command') {
    return <PayrollAgentPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'toa-transformer') {
    return <ClientTransformerPanel mission={mission} client="toa" onBack={onBack} />;
  }

  if (mission.id === 'esp-transformer') {
    return <ClientTransformerPanel mission={mission} client="esp" onBack={onBack} />;
  }

  if (mission.id === 'fourfifty-transformer') {
    return <ClientTransformerPanel mission={mission} client="fourfifty" onBack={onBack} />;
  }

  if (mission.id === 'ledger-pdf') {
    return <LedgerPdfPanel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'ma-report') {
    return <MaReportPanel mission={mission} kind="standard" onBack={onBack} />;
  }

  if (mission.id === 'ma-report-v2') {
    return <MaReportV2Panel mission={mission} onBack={onBack} />;
  }

  if (mission.id === 'ma-report-monthly') {
    return <MaReportPanel mission={mission} kind="monthly" onBack={onBack} />;
  }

  if (mission.id === 'as-workflow') {
    return <AsWorkflowPanel mission={mission} onBack={onBack} />;
  }

  return (
    <section
      style={{ borderTopColor: mission.color }}
      className="mx-auto max-w-[700px] rounded-2xl border border-border border-t-[6px] bg-card p-8 shadow-sm"
    >
      <Button
        variant="ghost"
        onClick={onBack}
        style={{ color: mission.color }}
        className="mb-5 h-auto gap-1.5 px-2 py-1.5 text-sm font-semibold hover:bg-transparent"
      >
        <ArrowLeftIcon className="size-5" /> All missions
      </Button>

      <div className="mb-6 flex items-start gap-4">
        <span
          style={{ backgroundColor: `${mission.color}26`, color: mission.color }}
          className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl"
        >
          <mission.icon className="h-7 w-7" />
        </span>
        <div>
          <h2 className="mb-1 text-xl font-bold text-foreground">{mission.title}</h2>
          <p className="text-sm text-muted-foreground">{mission.description}</p>
        </div>
      </div>

      <HowToUse steps={DEFAULT_STEPS} />

      <UploadBox
        accept={mission.accept}
        hintLabel={mission.maxSizeLabel}
        accentColor={mission.color}
        onFileSelected={setFile}
      />

      <StatusBar file={file} />
    </section>
  );
}

export default MissionPanel;
